/**
 * FolderService - Manages folder hierarchy for notes
 *
 * Obsidian-compatible folder structure:
 * - Notes have a `folder` property containing the path (e.g., "Campaigns/Session Notes")
 * - Folders can be virtual (derived from note paths) or explicitly created
 * - User-created folders are persisted in localStorage
 * - Supports nested folders with "/" separator
 */

const STORAGE_KEY_PREFIX = 'kb_folders_'

/**
 * Get localStorage key for a context
 */
function getStorageKey(context) {
  // Prefer vault-scoped storage when available to keep folder sets per vault
  if (context?.vaultId) return `${STORAGE_KEY_PREFIX}vault_${context.vaultId}`
  if (context?.campaignId) return `${STORAGE_KEY_PREFIX}campaign_${context.campaignId}`
  if (context?.characterId) return `${STORAGE_KEY_PREFIX}character_${context.characterId}`
  return `${STORAGE_KEY_PREFIX}global`
}

/**
 * Get user-created folders from localStorage
 * @param {Object} context - Campaign/character context
 * @returns {Set<string>} Set of folder paths
 */
export function getUserFolders(context) {
  try {
    const stored = localStorage.getItem(getStorageKey(context))
    if (stored) {
      return new Set(JSON.parse(stored))
    }
  } catch (e) {
    console.warn('Failed to load user folders:', e)
  }
  return new Set()
}

/**
 * Save user-created folders to localStorage
 * @param {Object} context - Campaign/character context
 * @param {Set<string>} folders - Set of folder paths
 */
export function saveUserFolders(context, folders) {
  try {
    localStorage.setItem(getStorageKey(context), JSON.stringify([...folders]))
  } catch (e) {
    console.warn('Failed to save user folders:', e)
  }
}

/**
 * Add a user-created folder
 * @param {Object} context - Campaign/character context
 * @param {string} folderPath - Folder path to add
 */
export function addUserFolder(context, folderPath) {
  if (!folderPath) return
  const folders = getUserFolders(context)
  // Add the folder and all parent paths
  const segments = parseFolderPath(folderPath)
  for (let i = 1; i <= segments.length; i++) {
    const path = joinFolderPath(segments.slice(0, i))
    folders.add(path)
  }
  saveUserFolders(context, folders)
}

/**
 * Remove a user-created folder
 * @param {Object} context - Campaign/character context
 * @param {string} folderPath - Folder path to remove
 */
export function removeUserFolder(context, folderPath) {
  if (!folderPath) return
  const folders = getUserFolders(context)
  // Remove the folder and all child folders
  for (const path of folders) {
    if (path === folderPath || isChildOf(path, folderPath)) {
      folders.delete(path)
    }
  }
  saveUserFolders(context, folders)
}

/**
 * Move/rename a folder and update all child folder paths
 * @param {Object} context - Campaign/character context
 * @param {string} oldPath - Original folder path
 * @param {string} newPath - New folder path
 * @returns {Map<string, string>} Map of old paths to new paths for updating notes
 */
export function moveUserFolder(context, oldPath, newPath) {
  if (!oldPath || oldPath === newPath) return new Map()

  const folders = getUserFolders(context)
  const pathMapping = new Map()

  // Find all folders that need to be updated (the folder and its children)
  const foldersToUpdate = []
  for (const path of folders) {
    if (path === oldPath || isChildOf(path, oldPath)) {
      foldersToUpdate.push(path)
    }
  }

  // Remove old paths and add new ones
  for (const path of foldersToUpdate) {
    folders.delete(path)

    // Calculate new path
    let updatedPath
    if (path === oldPath) {
      updatedPath = newPath
    } else {
      // Replace the old prefix with new prefix
      const suffix = path.slice(oldPath.length)
      updatedPath = newPath + suffix
    }

    if (updatedPath) {
      folders.add(updatedPath)
    }
    pathMapping.set(path, updatedPath || '')
  }

  // Ensure parent folders of new path exist
  if (newPath) {
    const segments = parseFolderPath(newPath)
    for (let i = 1; i < segments.length; i++) {
      const parentPath = joinFolderPath(segments.slice(0, i))
      folders.add(parentPath)
    }
  }

  saveUserFolders(context, folders)
  return pathMapping
}

/**
 * Parse a folder path into segments
 * @param {string} path - Folder path like "Campaigns/Session Notes"
 * @returns {string[]} Array of path segments
 */
export function parseFolderPath(path) {
  if (!path || typeof path !== 'string') return []
  return path
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * Join path segments into a folder path
 * @param {string[]} segments - Array of path segments
 * @returns {string} Joined path
 */
export function joinFolderPath(segments) {
  if (!Array.isArray(segments)) return ''
  return segments.filter(Boolean).join('/')
}

/**
 * Get parent folder path
 * @param {string} path - Current folder path
 * @returns {string} Parent path or empty string for root
 */
export function getParentPath(path) {
  const segments = parseFolderPath(path)
  if (segments.length <= 1) return ''
  return joinFolderPath(segments.slice(0, -1))
}

/**
 * Get folder name from path
 * @param {string} path - Folder path
 * @returns {string} Folder name (last segment)
 */
export function getFolderName(path) {
  const segments = parseFolderPath(path)
  return segments.length > 0 ? segments[segments.length - 1] : ''
}

/**
 * Check if path is a child of parent path
 * @param {string} childPath - Potential child path
 * @param {string} parentPath - Parent path to check against
 * @returns {boolean}
 */
export function isChildOf(childPath, parentPath) {
  if (!parentPath) return true // Everything is child of root
  if (!childPath) return false
  const child = parseFolderPath(childPath)
  const parent = parseFolderPath(parentPath)
  if (child.length <= parent.length) return false
  for (let i = 0; i < parent.length; i++) {
    if (child[i] !== parent[i]) return false
  }
  return true
}

/**
 * Check if path is direct child of parent (one level deep)
 */
export function isDirectChildOf(childPath, parentPath) {
  if (!isChildOf(childPath, parentPath)) return false
  const child = parseFolderPath(childPath)
  const parent = parseFolderPath(parentPath)
  return child.length === parent.length + 1
}

/**
 * Build folder tree from notes and user-created folders
 * @param {Array} notes - Array of note objects with optional `folder` property
 * @param {Object} options - Options for tree building
 * @param {Object} options.context - Campaign/character context for loading user folders
 * @param {Object} options.folderMeta - Folder metadata (icons, etc.) keyed by folder path
 * @returns {Array} Tree structure for QTree component
 */
export function buildFolderTree(notes, options = {}) {
  const {
    rootLabel = 'Notes',
    includeRootNotes = true,
    sortFoldersFirst = true,
    context = null,
    folderMeta = {},
    onlyMetaFolders = false
  } = options

  // Collect all unique folder paths (from notes + user-created)
  const folderPaths = new Set()
  const notesByFolder = new Map()

  // Load user-created folders (unless restricted to metadata-only)
  if (!onlyMetaFolders && context) {
    const userFolders = getUserFolders(context)
    userFolders.forEach((path) => folderPaths.add(path))
  }

  // Include folders present in vault metadata (icons/explicit markers) for cross-device sync
  if (folderMeta && typeof folderMeta === 'object') {
    Object.keys(folderMeta).forEach((p) => {
      if (!p) return
      // Add the path and all its parents so tree structure is complete
      const segments = parseFolderPath(p)
      for (let i = 1; i <= segments.length; i++) {
        const parentPath = joinFolderPath(segments.slice(0, i))
        folderPaths.add(parentPath)
      }
    })
  }

  // Initialize root
  notesByFolder.set('', [])

  // Process notes
  ;(notes || []).forEach((note) => {
    const folder = note.folder || ''

    // Add folder and all parent folders
    if (folder) {
      const segments = parseFolderPath(folder)
      for (let i = 1; i <= segments.length; i++) {
        const path = joinFolderPath(segments.slice(0, i))
        folderPaths.add(path)
        if (!notesByFolder.has(path)) {
          notesByFolder.set(path, [])
        }
      }
    }

    // Add note to its folder
    const key = folder || ''
    if (!notesByFolder.has(key)) {
      notesByFolder.set(key, [])
    }
    notesByFolder.get(key).push(note)
  })

  // Build tree recursively
  function buildNode(path, depth = 0) {
    const children = []
    const folderName = path ? getFolderName(path) : rootLabel
    const meta = path ? folderMeta[path] : null

    // Find direct child folders
    const childFolders = Array.from(folderPaths)
      .filter((p) => {
        if (!path) {
          // Root level - find folders with no parent
          const segments = parseFolderPath(p)
          return segments.length === 1
        }
        return isDirectChildOf(p, path)
      })
      .sort((a, b) => getFolderName(a).localeCompare(getFolderName(b)))

    // Add child folder nodes
    childFolders.forEach((childPath) => {
      children.push(buildNode(childPath, depth + 1))
    })

    // Add notes in this folder
    const folderNotes = notesByFolder.get(path || '') || []
    const sortedNotes = [...folderNotes].sort((a, b) => {
      const titleA = a.title || 'Untitled'
      const titleB = b.title || 'Untitled'
      return titleA.localeCompare(titleB)
    })

    sortedNotes.forEach((note) => {
      children.push({
        id: note.id,
        label: note.title || 'Untitled',
        icon: note.icon ? null : 'description', // Use custom icon if set
        customIcon: note.icon || null,
        nodeType: 'note',
        note: note,
        header: 'note',
        selectable: true
      })
    })

    // Sort: folders first if enabled
    if (sortFoldersFirst) {
      children.sort((a, b) => {
        const aIsFolder = a.nodeType === 'folder'
        const bIsFolder = b.nodeType === 'folder'
        if (aIsFolder && !bIsFolder) return -1
        if (!aIsFolder && bIsFolder) return 1
        return 0
      })
    }

    return {
      id: path || '__root__',
      label: folderName,
      icon: meta?.icon ? null : path ? 'folder' : 'source',
      customIcon: meta?.icon || null,
      explicit: !!(meta && meta.explicit === true),
      nodeType: 'folder',
      path: path || '',
      children: children, // Always include children array (even if empty)
      expandable: children.length > 0,
      header: 'folder',
      selectable: false
    }
  }

  // Build from root
  const root = buildNode('')

  // If we want to show root notes directly without a root folder
  if (!includeRootNotes || (root.children && root.children.length > 0)) {
    return root.children || []
  }

  return [root]
}

/**
 * Flatten tree to get all notes in order
 */
export function flattenTree(nodes, result = []) {
  if (!nodes) return result
  for (const node of nodes) {
    if (node.nodeType === 'note' && node.note) {
      result.push(node.note)
    }
    if (node.children) {
      flattenTree(node.children, result)
    }
  }
  return result
}

/**
 * Find node in tree by ID
 */
export function findNodeById(nodes, id) {
  if (!nodes || !id) return null
  for (const node of nodes) {
    if (node.id === id) return node
    if (node.children) {
      const found = findNodeById(node.children, id)
      if (found) return found
    }
  }
  return null
}

/**
 * Get all folder paths from tree
 */
export function getAllFolderPaths(nodes, result = []) {
  if (!nodes) return result
  for (const node of nodes) {
    if (node.nodeType === 'folder' && node.path) {
      result.push(node.path)
    }
    if (node.children) {
      getAllFolderPaths(node.children, result)
    }
  }
  return result
}

/**
 * Suggest folder paths for autocomplete
 */
export function suggestFolders(notes, query = '') {
  const paths = new Set()

  ;(notes || []).forEach((note) => {
    if (note.folder) {
      paths.add(note.folder)
      // Also add parent folders
      const segments = parseFolderPath(note.folder)
      for (let i = 1; i < segments.length; i++) {
        paths.add(joinFolderPath(segments.slice(0, i)))
      }
    }
  })

  const sorted = Array.from(paths).sort()
  if (!query) return sorted

  const q = query.toLowerCase()
  return sorted.filter((p) => p.toLowerCase().includes(q))
}

export default {
  parseFolderPath,
  joinFolderPath,
  getParentPath,
  getFolderName,
  isChildOf,
  isDirectChildOf,
  buildFolderTree,
  flattenTree,
  findNodeById,
  getAllFolderPaths,
  suggestFolders
}
